﻿import React, { useEffect, useRef, useState } from 'react';
import {
  fetchApplication,
  fetchApplicationDocument,
  fetchApplicationDocumentBody,
  fetchApplicationDocumentHistory,
  uploadApplicationDocument,
} from '~/store/covenants/thunks/applicationThunks.ts';
import {
  uploadDocumentSignature,
  deleteDocumentSignature,
  deleteApplicationDocument,
} from '~/store/covenants/thunks/documentThunks.ts';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '~/store/store.ts';
import {
  TApiApplicationDocument,
  TLocalFiles,
  TUseLocalFilesParams,
  TUseLocalFilesResult,
} from '~/modules/covenants/types.ts';
import {
  buildApplicationDocumentRowKey,
  buildApplicationDocumentStableKey,
  buildPendingUploadDocument,
  getUploadErrorMessage,
  isAllowedUploadFileExtension,
  isApplicationVersionMismatch,
} from '~/modules/covenants/utils.ts';
import {
  EMPTY_FILE_ERROR_MESSAGE,
  FILE_TOO_LARGE_ERROR_MESSAGE,
  MAX_FILE_SIZE,
} from '~/modules/covenants/constants.tsx';
import {
  fetchFinancialConditionCertificate,
  generateFinancialConditionCertificate,
  generateIncomeStructureByKvedCertificate,
  fetchStateAidCertificate,
  generateStateAidCertificate,
  saveFinancialConditionCertificate,
  fetchIncomeStructureByKvedCertificate,
  saveIncomeStructureByKvedCertificate,
  saveStateAidCertificate,
} from '~/store/covenants/thunks/certificateThunks.ts';
import {
  getCertificateDocumentFlags,
  getDocumentGenerationErrorMessages,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/utils.ts';
import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { serializeFinancialConditionCertificate } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/mapFormValuesToDto.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import { serializeIncomeStructureByKvedCertificate } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/mapFormValuesToDto.ts';
import type {
  StateAidCertificateType,
  TStateAidCertificateResponse,
  TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import { STATE_AID_CERTIFICATE_CONFIG } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/constants.ts';
import { mapStateAidFormValuesToDto } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/mapFormValuesToDto.ts';
import type {
  TCertificateSaveResult,
  TDocumentGenerationOptions,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import {
  selectDocumentGenerationBanks,
  selectDocumentGenerationCurrencies,
} from '~/store/covenants/selectors.ts';
import { completeCertificateGeneration } from '~/modules/covenants/CovenantsDataApplicationForm/hooks/documentGenerationUtils.ts';

export const useLocalFiles = ({
  contragentId,
  applicationId,
  applicationVersion,
  applicationDocuments,
  documentRows,
  onDocumentsRefresh,
  onVirtualRowConsumed,
  onVersionMismatch,
  onDocumentUploadStart,
  onDocumentUploadQueued,
  onDocumentUploadError,
  onSignatureActionStart,
  onSignatureActionQueued,
  onSignatureActionError,
  onSignatureUploadSuccess,
  allowedUploadFileExtensions,
  onUnsupportedFileExtension,
}: TUseLocalFilesParams): TUseLocalFilesResult => {
  const dispatch = useDispatch<AppDispatch>();
  const documentGenerationBanks = useSelector(selectDocumentGenerationBanks);
  const documentGenerationCurrencies = useSelector(selectDocumentGenerationCurrencies);
  const [localFiles, setLocalFiles] = useState<TLocalFiles>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sigFileInputRef = useRef<HTMLInputElement>(null);
  const latestVersionRef = useRef(applicationVersion);
  const [targetDocId, setTargetDocId] = useState<string | null>(null);
  const [signingDocId, setSigningDocId] = useState<string | null>(null);
  const signingDocRef = useRef<TApiApplicationDocument | null>(null);
  const pendingUploadDocumentsRef = useRef<Record<string, TApiApplicationDocument>>({});
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [documentGenerationError, setDocumentGenerationError] = useState<string[] | null>(null);

  useEffect(() => {
    latestVersionRef.current = applicationVersion;
  }, [applicationVersion]);

  const buildGroupKey = (doc: TApiApplicationDocument) => buildApplicationDocumentStableKey(doc);
  const buildRowKey = (doc: TApiApplicationDocument) => buildApplicationDocumentRowKey(doc);

  const findDocumentByRowKey = (rowKey: string) =>
    pendingUploadDocumentsRef.current[rowKey] ??
    documentRows.find((doc) => buildRowKey(doc) === rowKey) ??
    applicationDocuments.find((doc) => buildRowKey(doc) === rowKey);

  const findMainDocument = (rowKey: string) => {
    const directMatch = findDocumentByRowKey(rowKey);

    if (directMatch && !directMatch.isVirtualRow) {
      return directMatch;
    }

    const groupKey = directMatch
      ? buildGroupKey(directMatch)
      : rowKey.replace(/__(virtual|required).*/, '');

    return (
      documentRows.find(
        (doc) => buildGroupKey(doc) === groupKey && !doc.isVirtualRow && !!doc.id
      ) ??
      applicationDocuments.find(
        (doc) => buildGroupKey(doc) === groupKey && !doc.isVirtualRow && !!doc.id
      ) ??
      directMatch
    );
  };

  const clearLocalEntry = (rowKey: string) => {
    setLocalFiles((prev) => {
      if (!prev[rowKey]) {
        return prev;
      }

      const nextFiles = { ...prev };
      delete nextFiles[rowKey];
      return nextFiles;
    });
  };

  const clearPendingUploadDocument = (rowKey: string) => {
    if (!pendingUploadDocumentsRef.current[rowKey]) {
      return;
    }

    delete pendingUploadDocumentsRef.current[rowKey];
  };

  const refreshSingleDocument = async ({
    documentId,
    baseDocument,
    rowKey,
    consumeVirtualRow = false,
  }: {
    documentId: string;
    baseDocument: TApiApplicationDocument;
    rowKey?: string;
    consumeVirtualRow?: boolean;
  }) => {
    try {
      await dispatch(
        fetchApplicationDocument({
          contragentId,
          applicationId,
          id: documentId,
          baseDocument,
        })
      ).unwrap();
    } catch {
      await onDocumentsRefresh?.();
    }

    if (consumeVirtualRow && rowKey) {
      onVirtualRowConsumed?.(rowKey);
    }
  };

  const hasAttachedSignature = (rowKey: string, doc?: TApiApplicationDocument) => {
    const mainDoc = doc ?? findMainDocument(rowKey);
    const localEntry = localFiles[rowKey];

    if (!mainDoc) {
      return !!localEntry?.signature || !!localEntry?.isExternal;
    }

    return !!localEntry?.signature || !!localEntry?.isExternal || mainDoc.isCustomSignFile;
  };

  const clearLocalDocumentState = (stableKey: string) => {
    setLocalFiles((prev) => {
      const entry = prev[stableKey];

      if (!entry?.file) {
        return prev;
      }

      if (!entry.signature && !entry.isExternal) {
        const nextFiles = { ...prev };
        delete nextFiles[stableKey];
        return nextFiles;
      }

      return {
        ...prev,
        [stableKey]: {
          ...entry,
          file: undefined,
        },
      };
    });
  };

  const removeAttachedSignature = async (rowKey: string, doc?: TApiApplicationDocument) => {
    const mainDoc = doc ?? findMainDocument(rowKey);
    if (!mainDoc?.id || !hasAttachedSignature(rowKey, mainDoc)) return false;

    const response = await dispatch(
      deleteDocumentSignature({
        contragentId,
        applicationId,
        documentId: mainDoc.id,
        version: latestVersionRef.current,
      })
    ).unwrap();

    latestVersionRef.current = response.version;
    await refreshSingleDocument({
      documentId: mainDoc.id,
      baseDocument: mainDoc,
      rowKey,
    });
    clearLocalEntry(rowKey);
    return false;
  };

  const handleUploadForDoc = (rowKey: string) => {
    setSignatureError(null);
    setTargetDocId(rowKey);
    fileInputRef.current?.click();
  };

  const handleAddDocumentRow = (document: TApiApplicationDocument) => {
    const pendingDocument = buildPendingUploadDocument(document);
    const rowKey = buildRowKey(pendingDocument);

    pendingUploadDocumentsRef.current[rowKey] = pendingDocument;
    setSignatureError(null);
    setTargetDocId(rowKey);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && targetDocId) {
      if (file.size === 0) {
        setSignatureError(EMPTY_FILE_ERROR_MESSAGE);
        e.target.value = '';
        clearPendingUploadDocument(targetDocId);
        setTargetDocId(null);
        return;
      }

      if (!isAllowedUploadFileExtension(file, allowedUploadFileExtensions)) {
        onUnsupportedFileExtension?.();
        e.target.value = '';
        clearPendingUploadDocument(targetDocId);
        setTargetDocId(null);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setSignatureError(FILE_TOO_LARGE_ERROR_MESSAGE);
        e.target.value = '';
        clearPendingUploadDocument(targetDocId);
        setTargetDocId(null);
        return;
      }

      setLocalFiles((prev) => ({
        ...prev,
        [targetDocId]: {
          ...prev[targetDocId],
          file,
        },
      }));
      const docMeta = findDocumentByRowKey(targetDocId);

      if (!docMeta) {
        clearPendingUploadDocument(targetDocId);
        setTargetDocId(null);
        return;
      }

      try {
        onDocumentUploadStart?.();

        const response = await dispatch(
          uploadApplicationDocument({
            contragentId,
            applicationId,
            documentId: docMeta.documentId,
            childPeriod: docMeta.childPeriod,
            yearPeriod: docMeta.yearPeriod,
            deadline: docMeta.deadline || null,
            document: file,
            version: latestVersionRef.current,
          })
        ).unwrap();
        latestVersionRef.current = response.version;
        await refreshSingleDocument({
          documentId: response.id,
          baseDocument: docMeta,
          rowKey: targetDocId,
          consumeVirtualRow: !!docMeta.isVirtualRow,
        });
        clearLocalEntry(targetDocId);
        clearPendingUploadDocument(targetDocId);
        onDocumentUploadQueued?.();
        e.target.value = '';
        setTargetDocId(null);
      } catch (error) {
        onDocumentUploadError?.();
        clearLocalDocumentState(targetDocId);

        if (isApplicationVersionMismatch(error)) {
          onVersionMismatch?.();
          e.target.value = '';
          clearPendingUploadDocument(targetDocId);
          setTargetDocId(null);
          return;
        }

        setSignatureError(getUploadErrorMessage(error));
        e.target.value = '';
        clearPendingUploadDocument(targetDocId);
        setTargetDocId(null);
      }
    }
  };

  const handleSaveFinancialCondition = async (
    values: TFinancialConditionFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => {
    try {
      await dispatch(
        saveFinancialConditionCertificate({
          contragentId,
          applicationId,
          version,
          data: serializeFinancialConditionCertificate(
            values,
            documentGenerationBanks,
            documentGenerationCurrencies
          ),
        })
      ).unwrap();

      return 'success';
    } catch (error) {
      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
        return 'versionMismatch';
      }

      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return 'error';
    }
  };

  const handleLoadFinancialCondition = async (
    document: TApiApplicationDocument
  ): Promise<TFinancialConditionCertificateResponse | null> => {
    try {
      return await dispatch(
        fetchFinancialConditionCertificate({
          contragentId,
          applicationId,
          yearPeriod: document.yearPeriod,
          childPeriod: document.childPeriod,
        })
      ).unwrap();
    } catch (error) {
      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return null;
    }
  };

  const handleSaveIncomeStructureByKved = async (
    document: TApiApplicationDocument,
    values: TIncomeStructureByKvedFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => {
    try {
      await dispatch(
        saveIncomeStructureByKvedCertificate({
          contragentId,
          applicationId,
          yearPeriod: document.yearPeriod,
          childPeriod: document.childPeriod,
          version,
          data: serializeIncomeStructureByKvedCertificate(values),
        })
      ).unwrap();

      return 'success';
    } catch (error) {
      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
        return 'versionMismatch';
      }

      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return 'error';
    }
  };

  const handleLoadIncomeStructureByKved = async (
    document: TApiApplicationDocument
  ): Promise<TIncomeStructureByKvedCertificateResponse | null> => {
    try {
      return await dispatch(
        fetchIncomeStructureByKvedCertificate({
          contragentId,
          applicationId,
          yearPeriod: document.yearPeriod,
          childPeriod: document.childPeriod,
        })
      ).unwrap();
    } catch (error) {
      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return null;
    }
  };

  const handleSaveStateAidReceived = async (
    _document: TApiApplicationDocument,
    stateAidCertificateType: StateAidCertificateType,
    values: TStateAidFormValues,
    version: number
  ): Promise<TCertificateSaveResult> => {
    try {
      const config = STATE_AID_CERTIFICATE_CONFIG[stateAidCertificateType];
      await dispatch(
        saveStateAidCertificate({
          contragentId,
          applicationId,
          version,
          stateAidCertificateType,
          data: mapStateAidFormValuesToDto(values, {
            includeCovid: config.includesCovidSection,
          }),
        })
      ).unwrap();
      return 'success';
    } catch (error) {
      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
        return 'versionMismatch';
      }

      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return 'error';
    }
  };

  const handleLoadStateAidReceived = async (
    document: TApiApplicationDocument,
    stateAidCertificateType: StateAidCertificateType
  ): Promise<TStateAidCertificateResponse | null> => {
    try {
      return await dispatch(
        fetchStateAidCertificate({
          contragentId,
          applicationId,
          yearPeriod: document.yearPeriod,
          childPeriod: document.childPeriod,
          stateAidCertificateType,
        })
      ).unwrap();
    } catch (error) {
      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return null;
    }
  };

  const handleLoadDocumentHistory = async (
    document: TApiApplicationDocument,
    page: number,
    size: number
  ) => {
    try {
      return await dispatch(
        fetchApplicationDocumentHistory({
          applicationId,
          documentId: String(document.documentId),
          page,
          size,
        })
      ).unwrap();
    } catch (error) {
      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return null;
    }
  };

  const handleLoadDocumentBody = async (id: number) => {
    try {
      return await dispatch(fetchApplicationDocumentBody({ id })).unwrap();
    } catch (error) {
      setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
      return null;
    }
  };

  const finalizeRegeneration = async (source: TApiApplicationDocument) => {
    if (!source.id) {
      return true;
    }

    try {
      const application = await dispatch(
        fetchApplication({ contragentId, applicationId })
      ).unwrap();
      latestVersionRef.current = application.version;
      const response = await dispatch(
        deleteApplicationDocument({
          contragentId,
          applicationId,
          documentId: source.id,
          baseDocument: source,
          version: latestVersionRef.current,
        })
      ).unwrap();
      latestVersionRef.current = response.version;
      await onDocumentsRefresh?.();
      return true;
    } catch (error) {
      await onDocumentsRefresh?.();

      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
      } else {
        setDocumentGenerationError([
          'Новий документ створено, але попередній не вдалося видалити.',
        ]);
      }

      return false;
    }
  };

  const handleGenerateDocument = async (
    document: TApiApplicationDocument,
    options: TDocumentGenerationOptions = {}
  ): Promise<boolean> => {
    const generatedBaseDocument = options.regenerationSource
      ? { ...document, availableToRegenerate: false }
      : document;

    const runCertificateGeneration = async (
      generate: () => Promise<{ id: number }>
    ): Promise<boolean> => {
      try {
        onDocumentUploadStart?.();
        const isRegenerationComplete = await completeCertificateGeneration({
          generate,
          regenerationSource: options.regenerationSource,
          finalizeRegeneration,
          refreshGeneratedDocument: (documentId) =>
            refreshSingleDocument({ documentId, baseDocument: generatedBaseDocument }),
        });

        if (!isRegenerationComplete) {
          onDocumentUploadError?.();
          return true;
        }

        onDocumentUploadQueued?.();
        return true;
      } catch (error) {
        onDocumentUploadError?.();
        if (isApplicationVersionMismatch(error)) {
          onVersionMismatch?.();
          return false;
        }

        setDocumentGenerationError(getDocumentGenerationErrorMessages(error));
        return false;
      }
    };

    const { isFinancialCondition, isIncomeStructureByKved, isStateAidReceived } =
      getCertificateDocumentFlags(document.documentId);

    if (isFinancialCondition) {
      const isFop = options.isFop;
      if (typeof isFop !== 'boolean') {
        return false;
      }

      return runCertificateGeneration(() =>
        dispatch(
          generateFinancialConditionCertificate({
            contragentId,
            applicationId,
            isFop,
            yearPeriod: document.yearPeriod,
            childPeriod: document.childPeriod,
          })
        ).unwrap()
      );
    }

    if (isIncomeStructureByKved) {
      return runCertificateGeneration(() =>
        dispatch(
          generateIncomeStructureByKvedCertificate({
            contragentId,
            applicationId,
            yearPeriod: document.yearPeriod,
            childPeriod: document.childPeriod,
          })
        ).unwrap()
      );
    }

    if (!isStateAidReceived) {
      return false;
    }

    const stateAidCertificateType = options.stateAidCertificateType;
    if (!stateAidCertificateType) {
      return false;
    }

    return runCertificateGeneration(() =>
      dispatch(
        generateStateAidCertificate({
          contragentId,
          applicationId,
          yearPeriod: document.yearPeriod,
          childPeriod: document.childPeriod,
          stateAidCertificateType,
        })
      ).unwrap()
    );
  };

  const handleUploadSignatureClick = (rowKey: string, doc?: TApiApplicationDocument) => {
    setSignatureError(null);
    setSigningDocId(rowKey);
    signingDocRef.current = doc ?? findDocumentByRowKey(rowKey) ?? null;
    sigFileInputRef.current?.click();
  };

  const handleSignatureFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && signingDocId) {
      if (file.size === 0) {
        setSignatureError(EMPTY_FILE_ERROR_MESSAGE);
        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return false;
      }

      if (!file.name.toLowerCase().endsWith('.p7s')) {
        setSignatureError('Дозволено лише файли підпису з розширенням .p7s');
        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return false;
      }

      if (file.size > MAX_FILE_SIZE) {
        setSignatureError(FILE_TOO_LARGE_ERROR_MESSAGE);
        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return false;
      }

      setLocalFiles((prev) => ({
        ...prev,
        [signingDocId]: {
          ...prev[signingDocId],
          signature: file,
          isExternal: true,
        },
      }));

      const docMeta = signingDocRef.current ?? findDocumentByRowKey(signingDocId);
      if (!docMeta?.id) {
        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return false;
      }

      try {
        onSignatureActionStart?.();

        const signedFile = new FormData();
        signedFile.append('signedFile', file, file.name || 'signature.p7s');

        const response = await dispatch(
          uploadDocumentSignature({
            contragentId,
            applicationId,
            documentId: docMeta.id,
            signedFile,
            version: latestVersionRef.current,
          })
        ).unwrap();
        latestVersionRef.current = response.version;
        await refreshSingleDocument({
          documentId: docMeta.id,
          baseDocument: docMeta,
          rowKey: signingDocId,
        });
        onSignatureUploadSuccess?.(docMeta);
        clearLocalEntry(signingDocId);
        onSignatureActionQueued?.();

        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return true;
      } catch (error) {
        onSignatureActionError?.();
        setLocalFiles((prev) => ({
          ...prev,
          [signingDocId]: {
            ...prev[signingDocId],
            signature: undefined,
            isExternal: false,
          },
        }));

        if (isApplicationVersionMismatch(error)) {
          onVersionMismatch?.();
          e.target.value = '';
          setSigningDocId(null);
          signingDocRef.current = null;
          return false;
        }

        setSignatureError(getUploadErrorMessage(error));
        e.target.value = '';
        setSigningDocId(null);
        signingDocRef.current = null;
        return false;
      }
    }
    return false;
  };

  const handleDeleteSignature = async (rowKey: string, doc?: TApiApplicationDocument) => {
    const mainDoc = doc ?? findMainDocument(rowKey);
    if (!mainDoc?.id) return;

    try {
      onSignatureActionStart?.();
      await removeAttachedSignature(rowKey, mainDoc);
      onSignatureActionQueued?.();
    } catch (error) {
      onSignatureActionError?.();
      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
        return;
      }

      setSignatureError(getUploadErrorMessage(error));
    }
  };

  const handleDeleteDocument = async (rowKey: string) => {
    const doc = findDocumentByRowKey(rowKey);
    if (!doc) {
      return;
    }

    if (!doc.id || doc.isVirtualRow) {
      clearLocalEntry(rowKey);
      clearPendingUploadDocument(rowKey);
      onVirtualRowConsumed?.(rowKey);
      return;
    }

    try {
      const response = await dispatch(
        deleteApplicationDocument({
          contragentId,
          applicationId,
          documentId: doc.id,
          baseDocument: doc,
          version: latestVersionRef.current,
        })
      ).unwrap();
      latestVersionRef.current = response.version;
      clearLocalEntry(rowKey);
      clearPendingUploadDocument(rowKey);
    } catch (error) {
      if (isApplicationVersionMismatch(error)) {
        onVersionMismatch?.();
        return;
      }

      setSignatureError(getUploadErrorMessage(error));
    }
  };

  const hasFiles = !!Object.keys(localFiles).length;

  return {
    localFiles,
    fileInputRef,
    sigFileInputRef,
    hasFiles,
    handleUploadForDoc,
    handleAddDocumentRow,
    documentGenerationCallbacks: {
      saveFinancialCondition: handleSaveFinancialCondition,
      loadFinancialCondition: handleLoadFinancialCondition,
      saveIncomeStructureByKved: handleSaveIncomeStructureByKved,
      loadIncomeStructureByKved: handleLoadIncomeStructureByKved,
      saveStateAidReceived: handleSaveStateAidReceived,
      loadStateAidReceived: handleLoadStateAidReceived,
      loadHistory: handleLoadDocumentHistory,
      loadDocumentBody: handleLoadDocumentBody,
      generate: handleGenerateDocument,
    },
    handleFileChange,
    handleUploadSignatureClick,
    handleSignatureFileChange,
    handleDeleteSignature,
    handleDeleteDocument,
    signatureError,
    setSignatureError,
    documentGenerationError,
    setDocumentGenerationError,
    setSigningDocId,
    setTargetDocId,
  };
};
